import { defineStore } from 'pinia';
import { reactive, ref } from 'vue';
import { useApi } from './useApis';
import { createApiMap, type StatusType } from 'src/services/apiMap';
import { useDebounceFn } from '@vueuse/core';
import type { FilterKey, PeopleCardData, SearchType } from 'src/types';
import type { ProjectCardData } from 'src/types/entity.types';
import type { RecentAccessedData } from 'src/types/recent-accessed.types';
import type { SceneCardDataType } from 'src/types/scene.types';

// Constants
const DEFAULT_SEARCH_TAB: SearchType = 'recent_accessed';
const DEBOUNCE_DELAY = 500;

// Types
type SearchDataType = PeopleCardData;
export type SearchResultType = ProjectCardData | PeopleCardData | SceneCardDataType | RecentAccessedData;
export type StatusFilter = {
    assigned_to_me?: boolean | undefined;
    with_archived?: boolean | undefined;
    status_type?: StatusType[] | undefined;
    all_scene?: boolean | undefined;
    my_scene?: boolean | undefined;
};
type IntegrationKeyFunction =
    | 'openAccessSettings'
    | 'openEntitySettings'
    | 'openProjectSettings'
    | 'openSceneHub'
    | 'openPeopleInsight';
export const useGlobalSearchStore = defineStore('globalSearch', () => {
    // State

    const globalProperties = ref<Record<IntegrationKeyFunction | string, any>>({});
    const wsUserRootRoleId = ref<number | undefined>(undefined);
    const initialFilterValue = () => ({
        all_project: false,
        my_project: false,
        all_scene: wsUserRootRoleId.value !== 1,
        my_scene: wsUserRootRoleId.value == 1,
        common_assigned_to_me: false,
        archived: false,

        'scene_status:active': true,
        'scene_status:planned': true,
        'scene_status:completed': false,
        'project_status:active': true,
        'project_status:planned': true,
        'project_status:completed': false
    });
    const filterValue = ref<Partial<Record<FilterKey, boolean>>>({
        all_project: false,
        my_project: false,
        all_scene: wsUserRootRoleId.value !== 1,
        my_scene: wsUserRootRoleId.value == 1,
        common_assigned_to_me: false,
        archived: false,

        'scene_status:active': true,
        'scene_status:planned': true,
        'scene_status:completed': false,
        'project_status:active': true,
        'project_status:planned': true,
        'project_status:completed': false
    });
    const statusFilter = ref<StatusFilter>({});
    const searchText = ref<string>('');
    const searchResults = ref<SearchResultType[]>([]);
    const searchType = ref<string>('');
    const searchFilter = ref<Record<string, any>>({});
    const currentSearchTab = ref<SearchType>(DEFAULT_SEARCH_TAB);
    const isSearching = ref<boolean>(false);
    const internalHashmap = reactive<Record<string, SearchDataType | undefined>>({});
    const currentPage = ref<number>(0);
    const canLoadMore = ref<boolean>(true);

    // Utility Functions
    const resetHashmap = (): void => {
        Object.entries(internalHashmap).forEach(([key]) => {
            internalHashmap[key] = undefined;
        });
    };

    /**
     * Resets the search state to its initial values.
     *
     * This function performs the following actions:
     * - Resets the internal hashmap used for search.
     * - Clears the search results.
     * - Resets the search text to an empty string.
     * - Resets the search filter to an empty object.
     * - Sets the current page to the first page (0).
     * - Enables the ability to load more results.
     *
     * @returns {void} This function does not return a value.
     */
    const resetSearch = (): void => {
        resetHashmap();
        searchResults.value = [];
        searchText.value = '';
        searchFilter.value = {};
        currentPage.value = 0;
        canLoadMore.value = true;
    };
    const resetFilter = (): void => {
        filterValue.value = initialFilterValue();
    };
    const clearResult = (): void => {
        resetHashmap();
        searchResults.value = [];
        searchFilter.value = {};
        currentPage.value = 0;
    };

    const addData = (data: SearchDataType[]): void => {
        data.forEach((item) => {
            internalHashmap[item.id] = item;
        });
    };

    // API Integration
    const apiMap = createApiMap();
    const { makeRequest, cancelAllRequests, cancelSpecificRequest } = useApi();

    const performSearch = useDebounceFn(async ({ page = 1 }: { page?: number } & StatusFilter) => {
        try {
            isSearching.value = true;

            // Determine additional parameters based on the current search tab
            const include =
                currentSearchTab.value === 'item'
                    ? 'point_metric,assignees,entity.entity_type'
                    : currentSearchTab.value === 'recent_accessed'
                      ? 'item.point_metric,item.assignees,entity.entity_type'
                      : currentSearchTab.value === 'entity'
                        ? 'entity_type'
                        : undefined;
            const include_permission = ['item', 'recent_accessed', 'entity'].includes(currentSearchTab.value)
                ? 1
                : undefined;
            const order_by =
                currentSearchTab.value === 'recent_accessed' ? 'ws_user_recent_activity.updated_at-desc' : undefined;
            const { status_type, assigned_to_me, with_archived } = statusFilter.value;
            const config = {
                type: currentSearchTab.value,
                keyword: searchText.value.trim(),
                include_permission,
                order_by,
                include,
                page,
                status_type: status_type?.join(',') || undefined,
                assigned_to_me: assigned_to_me ? true : undefined,
                with_archived: with_archived ? true : undefined
            };

            // Configure the API request
            const getDataConfig = apiMap.searchData(config);

            // Make the API request and process the response
            const response = await makeRequest(getDataConfig);
            const result = response?.data?.map((item: SearchDataType) => ({ ...item })) || [];

            // Update the search results and internal state
            if (result.length > 0) {
                searchResults.value = [...searchResults.value, ...result];
                addData(result);
                canLoadMore.value = response.meta.current_page < response.meta.last_page; // Check if more data is available
            } else {
                canLoadMore.value = false; // No more data to load
            }

            return result;
        } catch (error) {
            canLoadMore.value = false;
            console.error('Error during search:', error);
        } finally {
            isSearching.value = false;
        }
        return [];
    }, DEBOUNCE_DELAY);

    const loadMoreDataFunc = async (): Promise<SearchResultType[]> => {
        const nextPage = currentPage.value + 1;
        const res = await performSearch({ page: nextPage });
        if (res) {
            currentPage.value = nextPage;
        }
        return res;
    };

    // Return store state and actions
    return {
        currentSearchTab,
        searchText,
        searchResults,
        searchType,
        searchFilter,
        statusFilter,
        isSearching,
        currentPage,
        canLoadMore,
        performSearch,
        resetSearch,
        cancelAllRequests,
        cancelSpecificRequest,
        addData,
        loadMoreDataFunc,
        clearResult,
        resetFilter,
        filterValue,
        wsUserRootRoleId,
        globalProperties
    };
});
